import { createContext, useContext, useEffect, useState, useSyncExternalStore, ReactNode } from 'react'

type Theme = 'light' | 'dark' | 'system'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
  effectiveTheme: 'light' | 'dark'
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const isTheme = (value: string | null): value is Theme => (
  value === 'light' || value === 'dark' || value === 'system'
)

const getStoredTheme = (): Theme => {
  if (typeof window === 'undefined') {
    return 'system'
  }

  const stored = window.localStorage.getItem('theme')
  return isTheme(stored) ? stored : 'system'
}

const getEffectiveTheme = (theme: Theme): 'light' | 'dark' => {
  if (typeof window === 'undefined') {
    return 'light'
  }

  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }

  return theme
}

const neverChanges = () => () => {}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme())
  /*
    False at build time and on the one render where React takes over markup a
    static page was built with, true on every other render.

    The build has no visitor, so it answers 'system' and 'light' (the two
    fallbacks above). The browser knows the stored choice straight away, and
    handing it out on that first render made the theme button differ from the
    built one: a different icon, which React treats as a failed takeover. It
    threw the whole public header away and drew it again, on every static
    page, for everybody who had ever picked a theme. So consumers get the
    build's answers for that one render and the real ones on the next. The
    state itself is never masked: the class on <html> is written from it.
  */
  const hasThePage = useSyncExternalStore(neverChanges, () => true, () => false)

  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>(() => getEffectiveTheme(getStoredTheme()))

  // Update effective theme based on theme setting and system preference
  useEffect(() => {
    const updateEffectiveTheme = () => {
      setEffectiveTheme(getEffectiveTheme(theme))
    }

    updateEffectiveTheme()

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => updateEffectiveTheme()

    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [theme])

  // Apply dark class to document root
  useEffect(() => {
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(effectiveTheme)
  }, [effectiveTheme])

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('theme', newTheme)
    }
  }

  return (
    <ThemeContext.Provider
      value={{
        theme: hasThePage ? theme : 'system',
        setTheme,
        effectiveTheme: hasThePage ? effectiveTheme : 'light',
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
